import { useState } from "react";
import { 
  FileText, 
  FileCheck, 
  ClipboardList, 
  CheckCircle2,
  Send
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const requestTypes = [
  "Insurance Certificate Request",
  "W-9 Request",
  "Compliance Documents",
  "Subcontractor Information",
  "Other"
];

const documentTypes = [
  {
    icon: FileCheck,
    title: "Insurance Certificate",
    description: "Request certificate of insurance for your records."
  },
  {
    icon: FileText,
    title: "W-9 Form",
    description: "Request our W-9 tax information form."
  },
  {
    icon: ClipboardList,
    title: "Compliance Documents",
    description: "Request safety and compliance documentation."
  }
];

export default function Vendor() {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    request_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, request_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/vendor/request`, formData);
      setSubmitted(true);
      toast.success("Your request has been submitted successfully!");
      setFormData({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        request_type: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting request:", error);
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="vendor-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#0F172A]" data-testid="vendor-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Vendor Portal
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tight text-white mt-4 mb-6">
              Vendor & Partner Resources
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              Request compliance documents, insurance certificates, and other 
              vendor-related information.
            </p>
          </div>
        </div>
      </section>

      {/* Document Types */}
      <section className="py-20 md:py-32 bg-white" data-testid="document-types">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Available Documents
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
              Request Documents
            </h2>
            <div className="section-line mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {documentTypes.map((doc, index) => (
              <div 
                key={index}
                className="bg-slate-50 border border-slate-200 p-8 text-center hover:border-[#00B4D8] transition-colors"
                data-testid={`doc-type-${index}`}
              >
                <div className="w-16 h-16 bg-[#0F172A] flex items-center justify-center mx-auto mb-6">
                  <doc.icon className="h-8 w-8 text-[#00B4D8]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                  {doc.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {doc.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Request Form */}
      <section className="py-20 md:py-32 bg-slate-50 grid-pattern" data-testid="vendor-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Submit Request
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
              Document Request Form
            </h2>
            <div className="section-line mx-auto"></div>
          </div>

          <div className="bg-white p-8 md:p-12 border border-slate-200">
            {submitted ? (
              <div className="text-center py-12" data-testid="vendor-form-success">
                <CheckCircle2 className="h-16 w-16 text-[#00B4D8] mx-auto mb-6" />
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                  Request Submitted!
                </h3>
                <p className="text-slate-600 mb-6">
                  Thank you for your request. Our team will process it and respond within 2 business days.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-[#00B4D8] font-semibold hover:underline"
                  data-testid="submit-another-vendor-button"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="vendor-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="company_name" className="text-slate-700 font-medium">
                      Company Name *
                    </Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                      placeholder="Your company name"
                      data-testid="vendor-input-company"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_name" className="text-slate-700 font-medium">
                      Contact Name *
                    </Label>
                    <Input
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      required
                      className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                      placeholder="Your full name"
                      data-testid="vendor-input-contact"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                      placeholder="you@company.com"
                      data-testid="vendor-input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 font-medium">
                      Phone *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                      placeholder="(555) 123-4567"
                      data-testid="vendor-input-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="request_type" className="text-slate-700 font-medium">
                    Request Type *
                  </Label>
                  <Select onValueChange={handleSelectChange} value={formData.request_type} required>
                    <SelectTrigger 
                      className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                      data-testid="vendor-select-type"
                    >
                      <SelectValue placeholder="Select request type" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {requestTypes.map((type) => (
                        <SelectItem key={type} value={type} className="cursor-pointer">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-700 font-medium">
                    Additional Information
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="mt-2 rounded-none border-slate-300 focus:border-[#00B4D8] focus:ring-[#00B4D8]"
                    placeholder="Any additional details about your request..."
                    data-testid="vendor-input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center bg-[#00B4D8] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,1)] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="vendor-submit-button"
                >
                  {isSubmitting ? (
                    "Submitting..."
                  ) : (
                    <>
                      Submit Request
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
